import React, { memo } from 'react';
import { Task, User, TaskPriority } from '../../types';
import { Modal, ConfirmModal } from '../common';
import { TaskChecklist } from './TaskChecklist';
import { PrioritySelector } from './PrioritySelector';
import { TaskFormState } from '../../hooks/useTaskActions';

// ============================================
// Types
// ============================================

interface TaskEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task;
  formState: TaskFormState;
  onFormChange: (updates: Partial<TaskFormState>) => void;
  onSave: () => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  users: User[];
  
  // Checklist props
  newItemText: string;
  onNewItemChange: (text: string) => void;
  onToggleChecklistItem: (id: string) => void;
  onRemoveChecklistItem: (id: string) => void;
  onAddChecklistItem: () => void;
  
  // State
  isSaving: boolean;
  isDeleting: boolean;
  canDelete: boolean;
  
  // Delete confirmation
  isDeleteConfirmOpen: boolean;
  onOpenDeleteConfirm: () => void;
  onCloseDeleteConfirm: () => void;
}

// ============================================
// Component
// ============================================

export const TaskEditModal: React.FC<TaskEditModalProps> = memo(({
  isOpen,
  onClose,
  task,
  formState,
  onFormChange,
  onSave,
  onDelete,
  users,
  newItemText,
  onNewItemChange,
  onToggleChecklistItem,
  onRemoveChecklistItem,
  onAddChecklistItem,
  isSaving,
  isDeleting,
  canDelete,
  isDeleteConfirmOpen,
  onOpenDeleteConfirm,
  onCloseDeleteConfirm,
}) => {
  const handleSave = async () => {
    const success = await onSave();
    if (success) {
      onClose();
    }
  };

  const handleDelete = async () => {
    const success = await onDelete();
    if (success) {
      onCloseDeleteConfirm();
      onClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Edit Task"
        size="lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Title */}
          <div className="md:col-span-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
              Title
            </label>
            <input 
              type="text" 
              value={formState.title}
              onChange={(e) => onFormChange({ title: e.target.value })}
              className="
                w-full px-5 py-3 md:py-4 rounded-2xl 
                border border-slate-200 bg-slate-50/50 
                outline-none font-black text-slate-900
                focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
                transition-all
              "
              placeholder="Task title..."
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
              Description
            </label>
            <textarea 
              value={formState.description}
              onChange={(e) => onFormChange({ description: e.target.value })}
              className="
                w-full px-5 py-3 md:py-4 rounded-2xl 
                border border-slate-200 bg-slate-50/50 
                outline-none h-24 resize-none font-medium
                focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
                transition-all
              "
              placeholder="Describe the task..."
            />
          </div>

          {/* Checklist */}
          <div className="md:col-span-2">
            <TaskChecklist
              items={formState.checklist}
              onToggle={onToggleChecklistItem}
              onRemove={onRemoveChecklistItem}
              onAdd={onAddChecklistItem}
              newItemText={newItemText}
              onNewItemChange={onNewItemChange}
            />
          </div>

          {/* Assignee */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
              Assignee
            </label>
            <select 
              value={formState.assigneeId}
              onChange={(e) => onFormChange({ assigneeId: e.target.value })}
              className="
                w-full px-5 py-3 md:py-4 rounded-2xl 
                border border-slate-200 bg-slate-50 
                font-bold outline-none cursor-pointer
                focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
                transition-all
              "
            >
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
              Priority
            </label>
            <PrioritySelector
              value={formState.priority}
              onChange={(priority) => onFormChange({ priority })}
            />
          </div>

          {/* Action Buttons */}
          <div className="md:col-span-2 flex flex-col md:flex-row gap-4 pt-6 border-t border-slate-100">
            {canDelete && (
              <button 
                type="button"
                onClick={onOpenDeleteConfirm}
                disabled={isSaving}
                className="
                  w-full md:w-auto px-6 py-4 
                  text-rose-600 font-black uppercase tracking-widest text-[10px] 
                  rounded-2xl border border-rose-100
                  hover:bg-rose-50 
                  disabled:opacity-50 disabled:cursor-not-allowed
                  order-3 md:order-1
                  transition-all
                "
              >
                Delete Task
              </button>
            )}
            
            <div className="flex flex-1 gap-3 order-1 md:order-2">
              <button 
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="
                  flex-1 py-4 
                  bg-slate-50 text-slate-600 
                  font-black uppercase text-[10px] rounded-2xl
                  hover:bg-slate-100
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all
                "
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleSave}
                disabled={isSaving || !formState.title.trim()}
                className="
                  flex-[2] py-4 
                  bg-indigo-600 text-white 
                  font-black uppercase text-[10px] rounded-2xl 
                  shadow-xl shadow-indigo-600/20 
                  hover:bg-indigo-700 active:scale-95
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all
                "
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={onCloseDeleteConfirm}
        onConfirm={handleDelete}
        title="Delete Task?"
        message="This will remove the task permanently from the cloud records."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant="danger"
      />
    </>
  );
});

TaskEditModal.displayName = 'TaskEditModal';

export default TaskEditModal;
